/**
 * Rig clip resolution (AP5.S1) — map the wire's `AnimState` onto whichever
 * animation clips a delivered rig actually ships, WITHOUT hardcoding one rig's
 * clip names.
 *
 * Why a resolver rather than a fixed table: the interim `player-rig-v1`
 * (AD-181) ships 19 Mixamo-style clips (`Idle_3`, `Walking`, `Running`,
 * `Basic_Jump`, …), while the Track-C rig and any future character are authored
 * by a different art path with their own naming (`Idle`, `Walk`, `Run`, …).
 * Binding the client to one rig's literal names would break the moment a new
 * character lands — exactly the kind of hardcoded content Principle 2 forbids —
 * so states resolve by scored pattern match and a new rig needs no code change.
 *
 * Scoring (lowest total wins, deterministic — no `Math.random`, no clock):
 *   - the pattern list for a state is ordered by preference; an earlier pattern
 *     beats a later one (`PATTERN_RANK` dominates the score),
 *   - within one pattern: exact name beats prefix beats substring,
 *   - ties break toward the SHORTER clip name, so `Idle_3` wins over
 *     `Idle_Turn_Left` and `Running` over `Run_Turn_Left` — the base locomotion
 *     clip rather than a directional variant.
 *
 * Pure + node-safe: no `three`, no DOM, no GPU — the whole resolver runs under
 * `node --test` (AD-120). Frontend-local convention like the other four rig
 * conventions (rig-avatar/index.ts); promoting it into `shared/` would be a T3
 * DR escalation. Nothing here rides the per-tick path (AD-031): clip selection
 * happens on an `AnimState` CHANGE, not per frame.
 *
 * Import boundary (AD-122): shared types via `@great-cloud/shared/client`.
 */

import { TICK_RATE, type AnimState } from '@great-cloud/shared/client';

/**
 * MIRROR of the backend's `PLAYER_ATTACK_COOLDOWN_TICKS` (backend/src/world/combat.ts):
 * ticks between basic attacks. The backend does not export it through
 * `@great-cloud/shared`, and the frontend may not import the backend, so the tick
 * count is mirrored here ONCE and pinned by a drift guard (actionClips.test.ts reads
 * the backend source and fails on any difference). The seconds are never written
 * down: they are derived from this and the shared `TICK_RATE`.
 */
export const PLAYER_ATTACK_COOLDOWN_TICKS_MIRROR = 9;

/** The server's swing cooldown in wall-clock seconds, derived (9 / 15 = 0.6 s today). */
export const PLAYER_ATTACK_COOLDOWN_SECONDS = PLAYER_ATTACK_COOLDOWN_TICKS_MIRROR / TICK_RATE;

/** The generic swing's hold budget, CLIP seconds (Left_Slash: both strokes by 1.5 s — §7.2). */
export const GENERIC_ATTACK_HOLD_SECONDS = 1.6;

/** The three wire animation states (`shared/sim/movement.ts`). */
export const ANIM_STATES: readonly AnimState[] = ['idle', 'move', 'dodge'];

/**
 * Ordered clip-name patterns per state, most-preferred first. Data, not logic —
 * an art path that names its clips differently is accommodated by extending
 * this table (or passing an override to `resolveClipMap`), never by branching.
 *
 * `dodge` deliberately falls back to a jump/leap: the interim rig ships no
 * dodge/roll clip, and a leap reads as an evade far better than a frozen bind
 * pose. Replaced automatically the moment a rig ships a real `dodge`/`roll`.
 */
export const DEFAULT_CLIP_PATTERNS: Readonly<Record<AnimState, readonly string[]>> = {
  idle: ['idle', 'stand', 'breath'],
  move: ['run', 'walk', 'jog', 'sprint', 'move'],
  dodge: ['dodge', 'roll', 'evade', 'dash', 'jump'],
};

/**
 * One-shot ACTION clips (AP5.S2) layered over the locomotion state — played once
 * on a client-side trigger (an `attack`/`telegraphResolve`/`entityDied` GameEvent,
 * a local gather/craft result) and then handed back to locomotion. NOT wire
 * states: nothing here changes `AnimState` or the protocol.
 */
export type RigAction = 'attack' | 'hit' | 'dead' | 'gather' | 'craft';

/** Every action, in a stable order (map construction + tests). */
export const RIG_ACTIONS: readonly RigAction[] = ['attack', 'hit', 'dead', 'gather', 'craft'];

/**
 * Ordered clip-name patterns per action, most-preferred first — the same
 * data-not-logic contract as `DEFAULT_CLIP_PATTERNS`. `craft` shares `collect`
 * with `gather` on purpose: the interim rig ships one "handle an object" clip
 * (`Collect_Object`) and no bench/forge clip, so crafting borrows it rather than
 * playing nothing; a rig that ships a real `craft`/`work` clip wins automatically.
 */
export const DEFAULT_ACTION_CLIP_PATTERNS: Readonly<Record<RigAction, readonly string[]>> = {
  attack: ['slash', 'attack', 'swing', 'strike'],
  hit: ['hit', 'react', 'flinch'],
  dead: ['dead', 'death', 'die'],
  gather: ['collect', 'gather', 'pick'],
  craft: ['collect', 'craft', 'work', 'use'],
};

/**
 * Which locomotion states carry the entity through the world (AP5.S2 fix,
 * 2026-09-02). The server owns position (AD-026), so while an entity's wire
 * state travels its rig must present the run/dodge: a one-shot holding a swing
 * or a crouch over that would slide the pose across the ground. Data, so a
 * future state (`swim`, `mount`) declares itself here rather than in a branch.
 */
export const STATE_TRAVELS: Readonly<Record<AnimState, boolean>> = {
  idle: false,
  move: true,
  dodge: true,
};

/** How one action shares the pose with locomotion (AP5.S2 fix, 2026-09-02). */
export interface RigActionPolicy {
  /**
   * Movement wins: the action neither STARTS while the entity's state travels
   * (locomotion keeps the pose — a swing cut on its next frame would be a flash,
   * not a swing) nor SURVIVES a transition to a travelling state (cut straight to
   * locomotion). `false` only for an action whose pose is right on a moving
   * entity — today just the clamped death, which the server never moves.
   */
  interruptedByTravel: boolean;
  /**
   * The longest the action may own the pose, in seconds, when its clip runs
   * longer: completion is reported here and locomotion returns. Absent ⇒ the
   * clip's own length. Set from the MEASURED motion profile of the shipped clip
   * (docs/systems/rig-animation.md §7.2), never from taste.
   *
   * ⚠ CLIP seconds, not wall-clock: it is compared against the action's own
   * playback head, so a `timeScale` changes how fast this much clip is shown,
   * never how much of the clip the budget covers.
   */
  holdSeconds?: number;
  /**
   * Start even while the entity's state reads as travelling (AD-636). Only for an
   * action whose lead-in the SERVER holds the entity still for — an enemy wind-up
   * (backend `zone.ts`: "Winding: hold position") — where a travelling state at the
   * trigger is the last pre-wind-up snapshot, i.e. presentation lag, not travel.
   * `interruptedByTravel` still cuts it on a later transition to travel.
   */
  startsWhileTravelling?: boolean;
  /**
   * Playback rate for the one-shot, as a multiple of the clip's authored speed.
   * Absent ⇒ 1 (the clip's own rate). > 1 plays it faster. Set from the MEASURED
   * relation between the clip's motion profile and the SERVER cadence the action
   * answers to (docs/systems/rig-animation.md §7.2), never from taste: a clip
   * authored longer than the cooldown that retriggers it can never show more
   * than its wind-up, however generous its `holdSeconds`.
   */
  timeScale?: number;
}

/**
 * Per-action policy. Measured on `player-rig-v1` (tools/rig/prove-animation.mjs
 * and the §7.2 motion-energy profile): `Left_Slash` is 3.17 s but both strokes
 * are over by 1.5 s (80 % of its motion energy; 90 % by 2.1 s) and the rest is a
 * slow settle the return fade replaces, so `attack` is budgeted at 1.6 s.
 * `Collect_Object` is 6.0 s and its motion spans the clip (crouch 0.5–1.3 s,
 * lift 2.6–4.8 s) — no budget cuts it cleanly, so `gather`/`craft` carry none;
 * movement cutting them is the cure for a moving collector. `Hit_Reaction_1`
 * is 1.23 s and needs none.
 *
 * `attack.timeScale` is the second measured term, and it is a SERVER relation
 * rather than a clip one (§7.7 follow-up 4, measured 2026-09-02: "mash at the
 * cooldown shows wind-ups only"). `Left_Slash` reaches the end of stroke one at
 * 1.0 s of clip; the server lets a player swing every
 * `PLAYER_ATTACK_COOLDOWN_TICKS = 9` ticks at `TICKS_PER_SECOND = 15`, i.e.
 * every 0.6 s (backend/src/world/combat.ts, shared/src/sim/ability.ts). At rate
 * 1 a swing restarted on every press replays the 0.5 s wind-up and rarely
 * reaches a stroke at all, so 1.0 / 0.6 = 5/3 is the rate at which one COMPLETE
 * stroke lands inside the server's own cadence. The budget is unchanged in clip
 * terms (still 1.6 s of clip, both strokes); only its wall-clock cost falls, to
 * 0.96 s. `[tune]` — amendable: §7.7 names an owner eyeball as the arbiter
 * between this and the two knobs it considered (a start offset, a restart gate).
 *
 * PER-CLASS PLAYER CLIPS (2026-09-23; §7.9): when the player rig carries a clip for
 * the equipped weapon class / gather kind / craft, `actionClips.ts` plays THAT clip
 * under a policy derived from its authored contact frame — this policy then applies
 * only to the fallback (`Left_Slash`, `Collect_Object`: an older rig, no weapon, an
 * unknown class). The attack rule there is `rate = max(5/3, contact / cooldown)`
 * with 5/3 read from THIS entry, cooldown = 9 ticks / 15 Hz = 0.6 s, no hold budget:
 *
 *   class      clip                  contact(clip s)  rate   contact(wall s)
 *   sword      Attack_sword_1h            0.533        1.667      0.320
 *   dagger     Attack_dagger              0.433        1.667      0.260
 *   shield     Attack_shield              0.600        1.667      0.360
 *   warhammer  Attack_warhammer_2h        1.233        2.055      0.600
 *   staff      Attack_staff               1.167        1.945      0.600
 *   bow        Attack_bow                 1.333        2.222      0.600
 *
 * Gather (`Gather_mine|chop|harvest`) and craft (`Craft_station`) play at rate 1,
 * whole: the server paces them per click, not on a cadence.
 */
export const DEFAULT_ACTION_POLICY: Readonly<Record<RigAction, RigActionPolicy>> = {
  // `q-player-attack-no-restart` (owner ruling 2026-09-25, rig-animation.md §9): the whole
  // budgeted swing fits the server cadence — 1.6 clip s in 0.6 s ⇒ 8/3 — so a swing is never
  // cut back to its start by the next one. Supersedes 5/3 (one stroke per cooldown), whose
  // eyeball §7.7 left open and the owner has now given: it "reads wrong".
  attack: {
    interruptedByTravel: true,
    holdSeconds: GENERIC_ATTACK_HOLD_SECONDS,
    timeScale: GENERIC_ATTACK_HOLD_SECONDS / PLAYER_ATTACK_COOLDOWN_SECONDS,
  },
  hit: { interruptedByTravel: true },
  dead: { interruptedByTravel: false },
  gather: { interruptedByTravel: true },
  craft: { interruptedByTravel: true },
};

/**
 * Policy for a per-attack MOTION clip (AD-636; docs/content/raid-schema.md §3.1a).
 * The clip starts at the wind-up's start and is authored to the data — its contact
 * frame sits at the served `windupMs` — so it plays at its own rate (no
 * `timeScale`) and whole (no `holdSeconds`: its recovery is authored, not a settle
 * to trim). Contrast `DEFAULT_ACTION_POLICY.attack`, which is measured against the
 * PLAYER's swing cooldown and has nothing to say about an enemy's telegraph.
 */
export const MOTION_ATTACK_POLICY: Readonly<RigActionPolicy> = {
  interruptedByTravel: true,
  startsWhileTravelling: true,
};

/**
 * The creature animator's per-action policy: `DEFAULT_ACTION_POLICY` with its own
 * `attack` — rate 1, whole (no `timeScale`, no `holdSeconds`). The generic `Attack`
 * plays at the telegraph's RESOLVE, so the server paces it by the enemy's cadence,
 * not the player's 0.6 s swing cooldown. Measured 2026-09-23 (H-1009): every rigged
 * creature (`codex-pilot/astra-burn-enemy-rigs/*`, 15 of 15) ships `Attack` at
 * 0.90 s; the shortest gap between two resolves is the fastest archetype's cooldown
 * (swarm, 18 ticks = 1.2 s at 15 Hz, backend/src/world/combat.ts) plus the next
 * wind-up, so a whole strike at rate 1 always ends before the next one. Under the
 * player policy the same clip flashed by in 0.54 s (the 1.6 s hold never cut it).
 * Motion clips keep `MOTION_ATTACK_POLICY` (AD-636); `hit`/`dead` are unchanged.
 */
export const CREATURE_ACTION_POLICY: Readonly<Record<RigAction, RigActionPolicy>> = {
  ...DEFAULT_ACTION_POLICY,
  attack: { interruptedByTravel: true },
};

/**
 * The rig's clip for one attack motion (AD-636), or `null` when the rig ships
 * none — the caller then keeps the generic `attack` presentation. The rig contract:
 * `Attack_<motion>`, plus `Attack_<motion>_heavy` where a rig carries both weights
 * of one motion, so a heavy attack prefers the heavy variant and falls back to the
 * plain name; a standard attack never borrows the heavy one. Names compare after
 * the resolver's own normalisation (case and `_`/`-`/`.` separators), and
 * EXACTLY — never by prefix, which would let `Attack_slam` answer for a motion
 * called `sla`, or a standard slam pick `Attack_slam_heavy`.
 */
export function resolveAttackMotionClip(
  motion: string,
  heavy: boolean,
  clipNames: readonly string[],
): string | null {
  const kind = normalise(motion);
  if (kind.length === 0) return null;
  const wanted = heavy ? [`attack ${kind} heavy`, `attack ${kind}`] : [`attack ${kind}`];
  for (const want of wanted) {
    const hit = clipNames.find((name) => normalise(name) === want);
    if (hit !== undefined) return hit;
  }
  return null;
}

/** Rank multiplier: any match on an earlier pattern beats every later pattern. */
const PATTERN_RANK = 1_000_000;
/** Match-kind bands within one pattern: exact < prefix < substring. */
const MATCH_EXACT = 0;
const MATCH_PREFIX = 1_000;
const MATCH_SUBSTRING = 10_000;

/** Normalise a clip name for matching: lower-case, separators stripped to spaces. */
function normalise(name: string): string {
  return name
    .toLowerCase()
    .replace(/[_\-.]+/g, ' ')
    .trim();
}

/**
 * The resolver's own clip-name normalisation, for a sibling that matches names
 * EXACTLY rather than by score (the per-class player clips, actionClips.ts) — one
 * definition of "the same name", not two.
 */
export function normaliseClipName(name: string): string {
  return normalise(name);
}

/**
 * Score one clip name against one pattern, or `null` when it does not match at
 * all. Lower is better. The clip-length term is what breaks `Idle_3` (short,
 * the base loop) ahead of `Idle_Turn_Left` (long, a directional variant).
 */
function scoreClip(clipName: string, pattern: string): number | null {
  const haystack = normalise(clipName);
  const needle = normalise(pattern);
  if (haystack.length === 0 || needle.length === 0) return null;
  const lengthTerm = haystack.length;
  if (haystack === needle) return MATCH_EXACT + lengthTerm;
  // Word-boundary prefix: "running" or "run turn left" both start with "run".
  if (haystack.startsWith(needle)) return MATCH_PREFIX + lengthTerm;
  const index = haystack.indexOf(needle);
  if (index >= 0) return MATCH_SUBSTRING + index * 10 + lengthTerm;
  return null;
}

/**
 * Pick the best clip name for one ordered pattern list, or `null` when nothing
 * matches. The single scorer behind both the state and the action resolvers.
 */
function resolveByPatterns(
  orderedPatterns: readonly string[],
  clipNames: readonly string[],
): string | null {
  let best: string | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (let rank = 0; rank < orderedPatterns.length; rank += 1) {
    const pattern = orderedPatterns[rank] as string;
    for (const clipName of clipNames) {
      const score = scoreClip(clipName, pattern);
      if (score === null) continue;
      const total = rank * PATTERN_RANK + score;
      // Strict `<` keeps the FIRST clip on a tie, so the result is stable for a
      // given clip order (determinism: the same rig always resolves the same).
      if (total < bestScore) {
        bestScore = total;
        best = clipName;
      }
    }
  }
  return best;
}

/**
 * Pick the best clip name for one state, or `null` when the rig ships nothing
 * plausible (the caller then leaves that state un-animated rather than playing
 * something wrong).
 */
export function resolveClipFor(
  state: AnimState,
  clipNames: readonly string[],
  patterns: Readonly<Record<AnimState, readonly string[]>> = DEFAULT_CLIP_PATTERNS,
): string | null {
  return resolveByPatterns(patterns[state] ?? [], clipNames);
}

/**
 * Pick the best one-shot clip for one action (AP5.S2), or `null` when the rig
 * ships nothing plausible — the animator then keeps locomotion playing rather than
 * flashing bind pose (the never-blank rule).
 */
export function resolveActionClipFor(
  action: RigAction,
  clipNames: readonly string[],
  patterns: Readonly<Record<RigAction, readonly string[]>> = DEFAULT_ACTION_CLIP_PATTERNS,
): string | null {
  return resolveByPatterns(patterns[action] ?? [], clipNames);
}

/** The resolved clip name per state; a state with no plausible clip maps to `null`. */
export type ClipMap = Readonly<Record<AnimState, string | null>>;

/**
 * Resolve every wire state against a rig's available clips. Called ONCE per
 * built avatar (not per frame) and handed to the animator.
 */
export function resolveClipMap(
  clipNames: readonly string[],
  patterns: Readonly<Record<AnimState, readonly string[]>> = DEFAULT_CLIP_PATTERNS,
): ClipMap {
  const map: Record<AnimState, string | null> = { idle: null, move: null, dodge: null };
  for (const state of ANIM_STATES) {
    map[state] = resolveClipFor(state, clipNames, patterns);
  }
  return map;
}

/** True when a rig resolved at least one clip — i.e. animating it is worthwhile. */
export function hasAnyClip(map: ClipMap): boolean {
  return ANIM_STATES.some((state) => map[state] !== null);
}

/** The resolved one-shot clip per action (AP5.S2); an action with no plausible clip maps to `null`. */
export type ActionClipMap = Readonly<Record<RigAction, string | null>>;

/**
 * Resolve every action against a rig's available clips. Called ONCE per built
 * avatar beside `resolveClipMap` and handed to the animator.
 */
export function resolveActionClipMap(
  clipNames: readonly string[],
  patterns: Readonly<Record<RigAction, readonly string[]>> = DEFAULT_ACTION_CLIP_PATTERNS,
): ActionClipMap {
  const map: Record<RigAction, string | null> = {
    attack: null,
    hit: null,
    dead: null,
    gather: null,
    craft: null,
  };
  for (const action of RIG_ACTIONS) {
    map[action] = resolveActionClipFor(action, clipNames, patterns);
  }
  return map;
}
